import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { FileSpreadsheet } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { classesQuery, yearsQuery } from '@/features/academic/api'
import { FormDialog } from '@/features/academic/components/FormDialog'
import { examTypesQuery, gradingScalesQuery, useSaveExam } from '../api'
import { examSchema, type ExamFormInput, type ExamFormOutput } from '../schemas'
import type { Exam } from '../types'

const FORM_ID = 'exam-form'
const FIELDS = [
  'name',
  'examTypeId',
  'gradingScaleId',
  'academicYearId',
  'startsOn',
  'endsOn',
  'weightPercent',
  'classIds',
] as const

interface ExamDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  exam?: Exam | null
}

export function ExamDialog({ open, onOpenChange, exam }: ExamDialogProps) {
  const { t } = useTranslation('exams')
  const save = useSaveExam()
  const navigate = useNavigate()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<FileSpreadsheet />}
      size="lg"
      title={exam ? t('Edit exam') : t('New exam')}
      description={t('Choose who sits the exam and how it is graded. Papers are added per class afterwards.')}
      formId={FORM_ID}
      submitLabel={exam ? t('Save changes') : t('Create exam')}
      pending={save.isPending}
    >
      <ExamForm
        exam={exam ?? null}
        onSubmit={async (values) => {
          const saved = await save.mutateAsync({ ...values, id: exam?.id })
          toast.success(exam ? t('Exam updated') : t('Exam created'), saved.name)
          onOpenChange(false)
          if (!exam) void navigate(`/admin/exams/${saved.id}`)
        }}
      />
    </FormDialog>
  )
}

function ExamForm({
  exam,
  onSubmit,
}: {
  exam: Exam | null
  onSubmit: (values: ExamFormOutput) => Promise<void>
}) {
  const { t } = useTranslation('exams')
  const types = useQuery(examTypesQuery())
  const scales = useQuery(gradingScalesQuery())
  const classes = useQuery(classesQuery())
  const years = useQuery(yearsQuery())
  const structureLocked = exam !== null && !['draft', 'scheduled'].includes(exam.status)

  const {
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<ExamFormInput, unknown, ExamFormOutput>({
    resolver: zodResolver(examSchema),
    defaultValues: {
      name: exam?.name ?? '',
      examTypeId: exam ? String(exam.examType.id) : '',
      gradingScaleId: exam ? String(exam.gradingScale.id) : '',
      academicYearId: exam ? String(exam.academicYear.id) : '',
      startsOn: exam?.startsOn ?? '',
      endsOn: exam?.endsOn ?? '',
      weightPercent:
        exam?.weightPercent !== null && exam?.weightPercent !== undefined ? String(exam.weightPercent) : '',
      classIds: exam?.classes.map((c) => c.id) ?? [],
    },
  })

  const defaultScale = scales.data?.find((s) => s.isDefault)
  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="grid gap-4 sm:grid-cols-2">
      <FormField label={t('Name')} error={errors.name?.message} required className="sm:col-span-2">
        <Input placeholder={t('e.g. Term 1 Midterm')} autoComplete="off" {...register('name')} />
      </FormField>
      <FormField label={t('Exam type')} error={errors.examTypeId?.message} required>
        <Select
          placeholder={t('Choose…')}
          options={(types.data ?? []).map((type) => ({ value: type.id, label: type.name }))}
          {...register('examTypeId')}
        />
      </FormField>
      <FormField label={t('Grading scale')} error={errors.gradingScaleId?.message}>
        <Select
          placeholder={
            defaultScale ? t('School default ({{name}})', { name: defaultScale.name }) : t('School default')
          }
          options={(scales.data ?? []).map((s) => ({
            value: s.id,
            label: s.isDefault ? t('{{name}} (default)', { name: s.name }) : s.name,
          }))}
          {...register('gradingScaleId')}
        />
      </FormField>
      <FormField
        label={t('Academic year')}
        error={errors.academicYearId?.message}
        hint={exam ? t('Cannot be changed after creation.') : t('Defaults to the current year.')}
      >
        <Select
          placeholder={t('Current year')}
          disabled={exam !== null}
          options={(years.data ?? []).map((y) => ({
            value: y.id,
            label: y.isCurrent ? t('{{name}} (current)', { name: y.name }) : y.name,
          }))}
          {...register('academicYearId')}
        />
      </FormField>
      <FormField
        label={t('Weight in final result (%)')}
        error={errors.weightPercent?.message}
        hint={t('Optional, for weighted term results.')}
      >
        <Input inputMode="decimal" placeholder={t('e.g. 30')} {...register('weightPercent')} />
      </FormField>
      <FormField label={t('First day')} error={errors.startsOn?.message} required>
        <Input type="date" {...register('startsOn')} />
      </FormField>
      <FormField label={t('Last day')} error={errors.endsOn?.message} required>
        <Input type="date" {...register('endsOn')} />
      </FormField>

      <fieldset
        className="sm:col-span-2"
        aria-describedby={errors.classIds ? 'exam-classes-error' : undefined}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <legend className="text-[0.8125rem] font-medium text-ink-soft">
            {t('Classes sitting the exam')}
            <span className="ml-0.5 text-danger-600" aria-hidden="true">
              *
            </span>
          </legend>
          {!structureLocked && classes.data?.length ? (
            <Button
              variant="link"
              size="sm"
              onClick={() =>
                setValue(
                  'classIds',
                  classes.data.map((c) => c.id),
                  { shouldValidate: true },
                )
              }
            >
              {t('Select all')}
            </Button>
          ) : null}
        </div>
        {classes.isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <Controller
            control={control}
            name="classIds"
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {(classes.data ?? []).map((c) => {
                  const checked = field.value.includes(c.id)
                  return (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-2.5 rounded-control border border-line px-3 py-2 text-sm text-ink-soft transition-colors hover:border-line-strong has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60 has-[[data-state=checked]]:border-brand-300 has-[[data-state=checked]]:bg-brand-50/60"
                    >
                      <Checkbox
                        checked={checked}
                        disabled={structureLocked}
                        onCheckedChange={(v) =>
                          field.onChange(
                            v === true ? [...field.value, c.id] : field.value.filter((id) => id !== c.id),
                          )
                        }
                      />
                      <span className="truncate">{c.name}</span>
                    </label>
                  )
                })}
              </div>
            )}
          />
        )}
        {structureLocked ? (
          <p className="mt-1.5 text-xs text-muted">{t('Classes are fixed once marks entry has opened.')}</p>
        ) : null}
        {errors.classIds ? (
          <p id="exam-classes-error" className="mt-1.5 text-xs font-medium text-danger-600" role="alert">
            {t(errors.classIds.message ?? '')}
          </p>
        ) : null}
      </fieldset>
    </form>
  )
}
